import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalBody,
  RadioButtonGroup,
  RadioButton,
  InlineNotification,
  FormGroup,
  InlineLoading,
} from '@carbon/react';
import styles from './allocation-method-update-modal.module.scss';

interface AllocationMethodUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdateAllocation: (allocationMethod: 'acreage' | 'lateral_length') => void;
  developmentAreaName: string;
  allocationMethod: 'acreage' | 'lateral_length' | null;
  isLoading?: boolean;
  isProcessingWorkflow?: boolean;
  error?: string | null;
}

type AllocationMethod = 'acreage' | 'lateral_length';

export const AllocationMethodUpdateModal: React.FC<
  AllocationMethodUpdateModalProps
> = ({
  isOpen,
  onClose,
  onUpdateAllocation,
  developmentAreaName,
  allocationMethod: currentAllocationMethod,
  isLoading = false,
  isProcessingWorkflow = false,
  error = null,
}) => {
  // Combined loading state for both initial request and workflow processing
  const isDisabled = isLoading || isProcessingWorkflow;
  // Determine default allocation method from prop
  const defaultAllocationMethod = useMemo<AllocationMethod>(() => {
    if (!currentAllocationMethod) return 'acreage';

    return currentAllocationMethod.toLowerCase() === 'acreage'
      ? 'acreage'
      : 'lateral_length';
  }, [currentAllocationMethod]);

  const [allocationMethod, setAllocationMethod] = useState<AllocationMethod>(
    defaultAllocationMethod
  );

  // Reset form when modal opens or allocation manager changes
  useEffect(() => {
    if (isOpen) {
      setAllocationMethod(defaultAllocationMethod);
    }
  }, [isOpen, defaultAllocationMethod]);

  const handleSave = () => {
    onUpdateAllocation(allocationMethod);
  };

  const handleClose = () => {
    if (!isDisabled) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      open={isOpen}
      passiveModal={false}
      hasScrollingContent
      size="md"
      modalHeading={
        <>
          <div className={styles.developmentAreaName}>
            {developmentAreaName}
          </div>
          <div className={styles.modalTitle}>
            Configure Unit Allocation Method
          </div>
        </>
      }
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onRequestClose={handleClose}
      onRequestSubmit={handleSave}
      onSecondarySubmit={handleClose}
      primaryButtonDisabled={isDisabled}
      preventCloseOnClickOutside={isDisabled}
      closeButtonLabel="Close"
      aria-label="Configure unit allocation method"
    >
      <ModalBody>
        {error && (
          <InlineNotification
            kind="error"
            title={error}
            lowContrast
            className={styles.errorNotification}
          />
        )}

        {isProcessingWorkflow ? (
          <div className={styles.workflowProcessingContainer}>
            <InlineLoading
              description="Updating allocation method and recalculating NRI values..."
              status="active"
            />
          </div>
        ) : (
          <>
            <div className={styles.warningNotificationContainer}>
              <InlineNotification
                kind="warning"
                title="Changing the Allocation Method may invalidate NRIs currently marked as correct."
                lowContrast
                hideCloseButton
                className={styles.warningNotification}
              />
            </div>

            <p className={styles.instructionalText}>
              Select how this unit allocates ownership across intersecting
              tracts. You can use Acreage or Lateral Length to determine how
              NRIs are calculated for Royalty and Working Interests.
            </p>

            <FormGroup
              className={styles.section}
              legendText="Allocation Method"
            >
              <RadioButtonGroup
                name="allocation-method"
                valueSelected={allocationMethod}
                onChange={(value: string | number | undefined) =>
                  setAllocationMethod(value as AllocationMethod)
                }
                orientation="vertical"
              >
                <RadioButton
                  labelText="Acreage"
                  value="acreage"
                  id="allocation-method-acreage"
                />
                <RadioButton
                  labelText="Lateral Length"
                  value="lateral_length"
                  id="allocation-method-lateral-length"
                />
              </RadioButtonGroup>
            </FormGroup>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};
